import { useEffect, useState } from "react";
import { Sparkles, X, Check, RefreshCw, FileText } from "lucide-react";
import { api } from "../lib/api";
import { useDashboardI18n } from "../lib/useDashboardI18n";

export default function RestoreBanner({ kind, onTurnOnTonight }) {
  const { t } = useDashboardI18n(kind);
  const [note, setNote] = useState(null);
  const [draftCount, setDraftCount] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [tonightDecided, setTonightDecided] = useState(false);
  const [turningOn, setTurningOn] = useState(false);

  useEffect(() => {
    const base = kind === "business" ? "/business/me/restore-state" : "/vendor/me/restore-state";
    api.get(base).then(({ data }) => {
      if (data?.restore_pending) setNote(data.restore_pending);
    }).catch(() => {});
    const draftsBase = kind === "business" ? "/business/me/recommendations/drafts" : "/vendor/me/recommendations/drafts";
    api.get(draftsBase).then(({ data }) => setDraftCount(data?.count || 0)).catch(() => setDraftCount(0));
  }, [kind]);

  if (!note || dismissed) return null;
  const previouslyHadHT = note.previously_had_here_tonight;
  const wasHidden = note.was_hidden;

  const dismiss = async () => {
    setDismissed(true);
    const base = kind === "business" ? "/business/me/restore-state" : "/vendor/me/restore-state";
    try { await api.delete(base); } catch { /* no-op */ }
  };
  const turnOnTonight = async () => {
    setTurningOn(true);
    try {
      await api.patch("/business/me", { here_tonight: true });
      setTonightDecided(true);
      onTurnOnTonight?.();
    } catch { /* no-op */ } finally { setTurningOn(false); }
  };
  const declineTonight = () => setTonightDecided(true);

  return (
    <div
      data-testid={`${kind}-restore-banner`}
      className="md:mt-2 mx-3 md:mx-0 rounded-xl p-4 mb-4"
      style={{
        background: "linear-gradient(135deg, rgba(232,184,75,0.12) 0%, rgba(232,184,75,0.04) 100%)",
        border: "1px solid rgba(232,184,75,0.45)",
        boxShadow: "0 6px 24px rgba(0,0,0,0.4)",
      }}
    >
      <div className="flex items-start gap-3">
        <span style={{
          width: 36, height: 36, borderRadius: 999, background: "#E8B84B",
          color: "#0B0B0B", display: "grid", placeItems: "center",
        }}>
          <Sparkles className="w-4 h-4" strokeWidth={1.5}/>
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg text-foreground">{t("restore_welcome_back")}</div>
          <div className="text-[13px] text-foreground/75 mt-1 leading-snug">
            {note.body || t("restore_default_body")}
          </div>
          {draftCount > 0 && (
            <div
              data-testid={`${kind}-restore-drafts`}
              className="mt-3 flex items-center gap-2 rounded-md px-3 py-2"
              style={{ background: "#171717", border: "1px solid rgba(232,184,75,0.3)" }}
            >
              <FileText className="w-3.5 h-3.5 text-gold" strokeWidth={1.5} />
              <span className="text-[12px] text-foreground/85">
                {draftCount === 1
                  ? t("restore_drafts_one", { count: draftCount })
                  : t("restore_drafts_many", { count: draftCount })}
              </span>
            </div>
          )}
          {kind === "business" && previouslyHadHT && !tonightDecided && (
            <div
              data-testid="business-restore-ht-prompt"
              className="mt-3 rounded-md p-3"
              style={{ background: "#171717", border: "1px solid rgba(232,184,75,0.3)" }}
            >
              <div className="text-[13px] text-foreground mb-2">
                {t("restore_ht_prompt")}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={turnOnTonight}
                  disabled={turningOn}
                  data-testid="business-restore-ht-yes"
                  className="inline-flex items-center justify-center gap-1.5 rounded-full"
                  style={{ background: "#E8B84B", color: "#0B0B0B", padding: "8px 14px", minHeight: 44, fontSize: 12 }}
                >
                  {turningOn ? <RefreshCw className="w-3 h-3 animate-spin" strokeWidth={1.5}/> : <Check className="w-3 h-3" strokeWidth={1.5}/>}
                  {t("restore_ht_yes")}
                </button>
                <button
                  type="button"
                  onClick={declineTonight}
                  data-testid="business-restore-ht-no"
                  className="text-foreground/65 hover:text-foreground"
                  style={{ padding: "8px 12px", minHeight: 44, fontSize: 12 }}
                >
                  {t("restore_ht_no")}
                </button>
              </div>
            </div>
          )}
          {wasHidden && (
            <div className="mt-3 text-[11px] text-gold/85" data-testid="business-restore-hidden-line">
              {t("restore_hidden_line")}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          data-testid={`${kind}-restore-dismiss`}
          style={{ color: "rgba(245,241,232,0.55)", padding: 6, minWidth: 40, minHeight: 40 }}
        >
          <X className="w-4 h-4" strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}
